import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { BaseSignalRHub } from '@nimbus-global/features/signalr/baseSignalR.hub';
import { IUserSessionLive } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import {
  IServiceSessionLive,
  IServiceSessionLiveByDistributionProfile,
} from '@nimbus-global/features/reportingLiveData/models/service-session.model';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { SIGNALR_DIAGNOSTIC_CLIENT } from '@nimbus-global/features/signalr/signalR.const';
import { IClientSideLogger, LogLevel } from '@nimbus-library/features/logger/services/client-logger/i-client-side-logger';
import { TwilioLiveMessage, TwilioMessageStatusUpdate } from '@nimbus-global/features/chat/models/chat-twilio.model';
import { CallRecordChangedNotification, CallRecordNotification } from '@nimbus-global/features/signalr/call-record-notification.model';
import { BulkEditOperationNotification } from '../bulk-edit/models/i-bulk-edit-operation-notification';
import { ForkRequestsService } from '../shared/fork-requests.service';
import { IUserTasksCapacity } from '../user-tasks-capacity/models/user-tasks-capacity.model';
import { IUserHeaderStateNotification } from './models/user-header-state-notification.model';
import { ITenantSettingsChangedNotification } from './models/tenant-settings-changed-notification.model';
import { ITaskFlowUpdatedNotification } from './models/task-flow-updated-notification.model';

@Injectable({
  providedIn: 'root',
})
export class NimbusSignalRHub extends BaseSignalRHub {
  private keyAddToGroupAsAdmin: string = 'AddToGroupAsAdmin';
  private keyAddToGroupsWithTeamId: string = 'AddToGroupsWithTeamId';
  private keyAddToGroupsWithTenantId: string = 'AddToGroupsWithTenantId';
  private keyAddToTenantBulkEditGroup: string = 'AddToTenantBulkEditGroup';
  private keyBulkEditOperationChanged: string = 'BulkEditOperationChanged';
  private keyConfigValuesChanged: string = 'ConfigValuesChanged';
  private keyFeaturesAll: string = 'FeaturesAll';
  private keyFeaturesChanged: string = 'FeaturesChanged';
  private keyRemoveFromGroupsWithTenantId: string = 'RemoveFromGroupsWithTenantId';
  private keyRemoveFromTenantBulkEditGroup: string = 'RemoveFromTenantBulkEditGroup';
  private keyRemoveFromGroupsWithTeamId: string = 'RemoveFromGroupsWithTeamId';
  private keyPermissionsChanged: string = 'PermissionsChanged';
  private keyServiceSessionsByDistributionProfileUpdated: string = 'ServiceSessionsByDistributionProfileUpdated';
  private keyServiceSessionsUpdated: string = 'ServiceSessionsUpdated';
  private keyUserSessionsUpdated: string = 'UserSessionsUpdated';
  private keyCallRecordCreated: string = 'CallRecordCreated';
  private keyCallRecordChanged: string = 'CallRecordChanged';
  private keyChatHandlerMessageAdded: string = 'ChatHandlerMessageAdded';
  private keyChatHandlerMessageDeliveryUpdate: string = 'ChatHandlerMessageDeliveryUpdate';
  private keyUserCapacityChanged: string = 'UserCapacityChanged';
  private keyUserTaskStateChanged: string = 'UserTaskStateChanged';
  private keyTenantSettingsChanged: string = 'TenantSettingsChanged';
  private keyTaskFlowUpdated: string = 'TaskFlowUpdated';

  protected signalRUrl: string = 'api/signalr-hub/v1/hub';

  public onBulkEditOperationNotification$: Subject<BulkEditOperationNotification> = new Subject<BulkEditOperationNotification>();
  public onConfigValuesChanged$: Subject<Record<string, string | null>> = new Subject<Record<string, string | null>>();
  public onFeaturesChanged$: Subject<IMap<boolean>> = new Subject<IMap<boolean>>();
  public onPermissionsChanged$: Subject<void> = new Subject();
  public onUserSessionsUpdated$: Subject<IUserSessionLive[]> = new Subject<IUserSessionLive[]>();
  public onServiceSessionsUpdated$: Subject<IServiceSessionLive[]> = new Subject<IServiceSessionLive[]>();
  public onServiceSessionsByDistributionProfileUpdated$: Subject<IServiceSessionLiveByDistributionProfile[]> = new Subject<
    IServiceSessionLiveByDistributionProfile[]
  >();
  public onCallRecordCreated$: Subject<CallRecordNotification> = new Subject<CallRecordNotification>();
  public onCallRecordChanged$: Subject<CallRecordChangedNotification> = new Subject<CallRecordChangedNotification>();
  public onChatHandlerMessageAdded$: Subject<TwilioLiveMessage> = new Subject<TwilioLiveMessage>();
  public onChatHandlerMessageStatusUpdated$: Subject<TwilioMessageStatusUpdate> = new Subject<TwilioMessageStatusUpdate>();
  public onUserCapacityChanged$: Subject<IUserTasksCapacity> = new Subject<IUserTasksCapacity>();
  public onUserTaskStateChanged$: Subject<IUserHeaderStateNotification> = new Subject<IUserHeaderStateNotification>();
  public onTenantSettingsChanged$: Subject<ITenantSettingsChangedNotification> = new Subject<ITenantSettingsChangedNotification>();
  public onTaskFlowUpdated$: Subject<ITaskFlowUpdatedNotification> = new Subject<ITaskFlowUpdatedNotification>();

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    protected readonly forkRequestService: ForkRequestsService,
    @Optional() @Inject(SIGNALR_DIAGNOSTIC_CLIENT) signalRDiagnosticClient: boolean,
    @Optional() clientSideLogger: IClientSideLogger,
  ) {
    super(forkRequestService, signalRDiagnosticClient, clientSideLogger);
    const isReadyToConnect$ = this.authenticationService.isAuthenticated$.pipe(distinctUntilChanged());
    this.initialize(isReadyToConnect$);
  }

  public addToGroupAsAdmin<TResult>(): Observable<TResult> {
    return this.invoke(this.keyAddToGroupAsAdmin);
  }

  public addToGroupsWithTenantId<TResult>(tenantIds: string[]): Observable<TResult> {
    return this.invokeBatchWithArguments(this.keyAddToGroupsWithTenantId, tenantIds);
  }

  public removeFromGroupsWithTenantId<TResult>(tenantIds: string[]): Observable<TResult> {
    return this.invokeBatchWithArguments(this.keyRemoveFromGroupsWithTenantId, tenantIds);
  }

  public addToTenantBulkEditGroup<TResult>(tenantId: string): Observable<TResult> {
    return this.invokeWithArguments(this.keyAddToTenantBulkEditGroup, tenantId);
  }

  public removeFromTenantBulkEditGroup<TResult>(tenantId: string): Observable<TResult> {
    return this.invokeWithArguments(this.keyRemoveFromTenantBulkEditGroup, tenantId);
  }

  public addToGroupsWithTeamId<TResult>(teamIds: string[], nimbusPermission: NimbusPermission): Observable<TResult> {
    return this.invokeBatchWithArguments(this.keyAddToGroupsWithTeamId, teamIds, nimbusPermission);
  }

  public removeFromGroupsWithTeamId<TResult>(teamIds: string[], nimbusPermission: NimbusPermission): Observable<TResult> {
    return this.invokeBatchWithArguments(this.keyRemoveFromGroupsWithTeamId, teamIds, nimbusPermission);
  }

  protected onConnected(): void {}

  protected onConnectedFailed(): void {}

  protected subscribeEvents(): void {
    this.subscribeEvent(this.keyBulkEditOperationChanged, this.bulkEditOperationChanged.bind(this));
    this.subscribeEvent(this.keyConfigValuesChanged, this.configValuesChanged.bind(this));
    this.subscribeEvent(this.keyFeaturesAll, this.featuresChanged.bind(this));
    this.subscribeEvent(this.keyFeaturesChanged, this.featuresChanged.bind(this));
    this.subscribeEvent(this.keyPermissionsChanged, this.permissionsChanged.bind(this));
    this.subscribeEvent(this.keyUserSessionsUpdated, this.userSessionsUpdated.bind(this));
    this.subscribeEvent(this.keyServiceSessionsUpdated, this.serviceSessionsUpdated.bind(this));
    this.subscribeEvent(this.keyServiceSessionsByDistributionProfileUpdated, this.serviceSessionsByDistributionProfileUpdated.bind(this));
    this.subscribeEvent(this.keyCallRecordCreated, this.callRecordCreated.bind(this));
    this.subscribeEvent(this.keyCallRecordChanged, this.callRecordChanged.bind(this));
    this.subscribeEvent(this.keyChatHandlerMessageAdded, this.chatHandlerMessageAdded.bind(this));
    this.subscribeEvent(this.keyChatHandlerMessageDeliveryUpdate, this.chatHandlerMessageDeliveryUpdate.bind(this));
    this.subscribeEvent(this.keyUserCapacityChanged, this.userCapacityChanged.bind(this));
    this.subscribeEvent(this.keyUserTaskStateChanged, this.userTaskStateChanged.bind(this));
    this.subscribeEvent(this.keyTenantSettingsChanged, this.tenantSettingsChanged.bind(this));
    this.subscribeEvent(this.keyTaskFlowUpdated, this.taskFlowUpdated.bind(this));
  }

  protected unsubscribeEvents(): void {
    this.unsubscribeEvent(this.keyBulkEditOperationChanged);
    this.unsubscribeEvent(this.keyConfigValuesChanged);
    this.unsubscribeEvent(this.keyFeaturesAll);
    this.unsubscribeEvent(this.keyFeaturesChanged);
    this.unsubscribeEvent(this.keyPermissionsChanged);
    this.unsubscribeEvent(this.keyUserSessionsUpdated);
    this.unsubscribeEvent(this.keyServiceSessionsUpdated);
    this.unsubscribeEvent(this.keyServiceSessionsByDistributionProfileUpdated);
    this.unsubscribeEvent(this.keyCallRecordCreated);
    this.unsubscribeEvent(this.keyCallRecordChanged);
    this.unsubscribeEvent(this.keyChatHandlerMessageAdded);
    this.unsubscribeEvent(this.keyChatHandlerMessageDeliveryUpdate);
    this.unsubscribeEvent(this.keyUserCapacityChanged);
    this.unsubscribeEvent(this.keyUserTaskStateChanged);
    this.unsubscribeEvent(this.keyTenantSettingsChanged);
    this.unsubscribeEvent(this.keyTaskFlowUpdated);
  }

  protected acquireIdToken(): Observable<string> {
    return this.authenticationService.aquireIdToken$().pipe(
      take(1),
      filter((x) => !!x),
      map((x) => x.token),
    );
  }

  private bulkEditOperationChanged(notification: BulkEditOperationNotification): void {
    this.onBulkEditOperationNotification$.next(notification);
  }

  private featuresChanged(features: IMap<boolean>): void {
    this.onFeaturesChanged$.next(features);
  }

  private configValuesChanged(values: Record<string, string | null>): void {
    this.onConfigValuesChanged$.next(values);
  }

  private permissionsChanged(): void {
    this.clientSideLogger?.writeMessage(LogLevel.Info, 'SignalR', 'Permissions changed');
    this.onPermissionsChanged$.next();
  }

  private userSessionsUpdated(data: IUserSessionLive[]): void {
    this.clientSideLogger?.writeMessage(
      LogLevel.Info,
      'SignalR',
      `SignalR Event. EventName: UserSessionUpdated: ${JSON.stringify(
        data.map((d) => ({
          id: d.id,
          unifiedConversationId: d.unifiedConversationId,
          state: d.state,
          orderNumber: d.orderNumber,
        })),
      )}`,
    );
    this.onUserSessionsUpdated$.next(data);
  }

  private serviceSessionsUpdated(data: IServiceSessionLive[]): void {
    this.clientSideLogger?.writeMessage(
      LogLevel.Info,
      'SignalR',
      `SignalR Event. EventName: ServiceSessionUpdated: ${JSON.stringify(
        data.map((d) => ({
          id: d.conversationId,
          unifiedConversationId: d.unifiedConversationId,
          state: d.state,
          orderNumber: d.orderNumber,
        })),
      )}`,
    );
    this.onServiceSessionsUpdated$.next(data);
  }

  private serviceSessionsByDistributionProfileUpdated(data: IServiceSessionLiveByDistributionProfile[]): void {
    this.clientSideLogger?.writeMessage(
      LogLevel.Info,
      'SignalR',
      `SignalR Event. EventName: ServiceSessionUpdatedByDistributionProfile: ${JSON.stringify(
        data.map((d) => ({
          id: d.serviceSession.conversationId,
          unifiedConversationId: d.serviceSession.unifiedConversationId,
          state: d.serviceSession.state,
          orderNumber: d.serviceSession.orderNumber,
          distributionProfileState: d.distributionProfileState,
        })),
      )}`,
    );
    this.onServiceSessionsByDistributionProfileUpdated$.next(data);
  }

  private callRecordCreated(data: CallRecordNotification): void {
    this.onCallRecordCreated$.next(data);
  }

  private callRecordChanged(data: CallRecordChangedNotification): void {
    this.onCallRecordChanged$.next(data);
  }

  private chatHandlerMessageAdded(data: TwilioLiveMessage): void {
    this.clientSideLogger?.writeMessage(
      LogLevel.Info,
      'SignalR',
      `SignalR Event. EventName: ChatHandlerMessageAdded: ${JSON.stringify(data)}`,
    );
    this.onChatHandlerMessageAdded$.next(data);
  }

  private chatHandlerMessageDeliveryUpdate(data: TwilioMessageStatusUpdate): void {
    this.clientSideLogger?.writeMessage(
      LogLevel.Info,
      'SignalR',
      `SignalR Event. EventName: ChatHandlerMessageDeliveryUpdate: ${JSON.stringify(data)}`,
    );
    this.onChatHandlerMessageStatusUpdated$.next(data);
  }

  private userCapacityChanged(data: IUserTasksCapacity): void {
    this.onUserCapacityChanged$.next(data);
  }

  private userTaskStateChanged(data: IUserHeaderStateNotification): void {
    this.onUserTaskStateChanged$.next(data);
  }

  private taskFlowUpdated(data: ITaskFlowUpdatedNotification): void {
    this.onTaskFlowUpdated$.next(data);
  }

  private tenantSettingsChanged(data: ITenantSettingsChangedNotification): void {
    this.onTenantSettingsChanged$.next(data);
  }
}
